import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useConfetti } from '../hooks/useConfetti';
import { Button } from '../components/ui/button';
import {
  Sparkles, Trophy, Users, Briefcase, PoundSterling, TrendingUp,
  Award, MapPin, Calendar, Crown, Heart, Download, ChevronDown,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (v) => v
  ? new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v)
  : '£0';

const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const postcodeArea = (pc) => {
  const m = (pc || '').toUpperCase().match(/^([A-Z]{1,2})/);
  return m ? m[1] : '';
};

const yearOf = (iso) => {
  if (!iso) return null;
  const y = parseInt(iso.slice(0, 4), 10);
  return Number.isNaN(y) ? null : y;
};

const monthOf = (iso) => {
  if (!iso || iso.length < 7) return null;
  const m = parseInt(iso.slice(5, 7), 10);
  return Number.isNaN(m) ? null : m - 1;
};

// ── Card wrapper with snap-scroll & gradient background ─────────────────────
const Card = ({ children, gradient, id }) => (
  <section
    id={id}
    className={`min-h-screen snap-start flex items-center justify-center p-6 sm:p-12 ${gradient}`}
  >
    <div className="max-w-5xl w-full">{children}</div>
  </section>
);

const Big = ({ children }) => (
  <p className="text-5xl sm:text-7xl lg:text-8xl font-bold text-white mb-4 leading-none" style={{ fontFamily: 'Plus Jakarta Sans' }}>
    {children}
  </p>
);

const Label = ({ children }) => (
  <p className="text-lg sm:text-xl text-white/80 uppercase tracking-widest font-medium mb-2">{children}</p>
);

const Sub = ({ children }) => (
  <p className="text-xl sm:text-2xl text-white/90 mt-4 max-w-2xl">{children}</p>
);

// ── Component ───────────────────────────────────────────────────────────────

const YearInReview = () => {
  const fireConfetti = useConfetti();
  const [clients, setClients] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [yearMenuOpen, setYearMenuOpen] = useState(false);
  const confettiFired = useRef(false);

  const authHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
  }), []);

  useEffect(() => {
    (async () => {
      try {
        const [cr, csr] = await Promise.all([
          fetch(`${API_URL}/api/clients?limit=2000`, { headers: authHeaders() }),
          fetch(`${API_URL}/api/cases?limit=2000`,   { headers: authHeaders() }),
        ]);
        const cd  = cr.ok  ? await cr.json()  : {};
        const csd = csr.ok ? await csr.json() : {};
        setClients(cd.clients || []);
        setCases(csd.cases || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [authHeaders]);

  // Available years from data
  const availableYears = useMemo(() => {
    const ys = new Set([new Date().getFullYear()]);
    clients.forEach(c => { const y = yearOf(c.created_at); if (y) ys.add(y); });
    cases.forEach(c => {
      [c.created_at, c.date_application_submitted, c.commission_paid_date].forEach(d => {
        const y = yearOf(d); if (y) ys.add(y);
      });
    });
    return Array.from(ys).sort((a, b) => b - a);
  }, [clients, cases]);

  // ── Year-scoped data ──────────────────────────────────────────────────────

  const yearData = useMemo(() => {
    // Cases submitted this year (by application submission date, fallback created_at)
    const yCasesSubmitted = cases.filter(c => {
      const y = yearOf(c.date_application_submitted) ?? yearOf(c.created_at);
      return y === year;
    });

    // "Active clients" this year: distinct clients who had a case submitted in this year.
    // (created_at is unreliable because records were often migrated in bulk.)
    const activeClientIds = new Set(yCasesSubmitted.map(c => c.client_id).filter(Boolean));
    const yClients = clients.filter(c => activeClientIds.has(c.client_id));

    // Cases completed this year — prefer commission_paid_date as the true completion signal,
    // fall back to expected_completion_date. Don't use updated_at (it's just last edit time).
    const yCompletions = cases.filter(c => {
      if (c.status !== 'completed') return false;
      const y = yearOf(c.commission_paid_date) ?? yearOf(c.expected_completion_date);
      return y === year;
    });

    // Commissions paid this year
    const yPaid = cases.filter(c => c.commission_status === 'paid' && yearOf(c.commission_paid_date) === year);
    const totalCommission = yPaid.reduce((s, c) => s + (c.gross_commission || 0) + (c.client_fee || 0), 0);

    // Biggest deal this year
    const biggest = [...yCasesSubmitted].sort((a, b) => (b.loan_amount || 0) - (a.loan_amount || 0))[0];

    // Top mortgage lender
    const mortgageLenderCounts = {};
    yCasesSubmitted.filter(c => c.product_type === 'mortgage' && c.lender_name).forEach(c => {
      mortgageLenderCounts[c.lender_name] = (mortgageLenderCounts[c.lender_name] || 0) + 1;
    });
    const topMortgageLenders = Object.entries(mortgageLenderCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);

    // Top insurance provider
    const insLenderCounts = {};
    yCasesSubmitted.filter(c => c.product_type === 'insurance').forEach(c => {
      const name = c.insurance_provider || c.lender_name;
      if (name) insLenderCounts[name] = (insLenderCounts[name] || 0) + 1;
    });
    const topInsuranceLenders = Object.entries(insLenderCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);

    // Busiest month (applications submitted)
    const monthCounts = Array(12).fill(0);
    yCasesSubmitted.forEach(c => {
      const m = monthOf(c.date_application_submitted || c.created_at);
      if (m !== null) monthCounts[m] += 1;
    });
    const busiestMonth = monthCounts.indexOf(Math.max(...monthCounts));

    // Best commission month
    const monthCommission = Array(12).fill(0);
    yPaid.forEach(c => {
      const m = monthOf(c.commission_paid_date);
      if (m !== null) monthCommission[m] += (c.gross_commission || 0) + (c.client_fee || 0);
    });
    const bestCommissionMonth = monthCommission.indexOf(Math.max(...monthCommission));

    // Lead source breakdown
    const sourceCounts = {};
    yClients.forEach(c => {
      const src = c.lead_source || 'unknown';
      sourceCounts[src] = (sourceCounts[src] || 0) + 1;
    });
    const sortedSources = Object.entries(sourceCounts).sort((a, b) => b[1] - a[1]);

    // Top referral partner
    const partnerCounts = {};
    yClients.filter(c => c.referral_partner_name).forEach(c => {
      partnerCounts[c.referral_partner_name] = (partnerCounts[c.referral_partner_name] || 0) + 1;
    });
    const topPartner = Object.entries(partnerCounts).sort((a, b) => b[1] - a[1])[0];

    // Geographic spread
    const areaCounts = {};
    yClients.forEach(c => {
      const a = postcodeArea(c.postcode);
      if (a) areaCounts[a] = (areaCounts[a] || 0) + 1;
    });
    const topArea = Object.entries(areaCounts).sort((a, b) => b[1] - a[1])[0];
    const uniqueAreas = Object.keys(areaCounts).length;

    // Client mix (mortgage type from yCasesSubmitted)
    const typeCounts = {};
    yCasesSubmitted.filter(c => c.product_type === 'mortgage' && c.mortgage_type).forEach(c => {
      typeCounts[c.mortgage_type] = (typeCounts[c.mortgage_type] || 0) + 1;
    });
    const sortedTypes = Object.entries(typeCounts).sort((a, b) => b[1] - a[1]);

    // ── Previous year for growth calc ──
    const prevYear = year - 1;
    const prevCasesArr = cases.filter(c => {
      const y = yearOf(c.date_application_submitted) ?? yearOf(c.created_at);
      return y === prevYear;
    });
    const prevCases = prevCasesArr.length;
    const prevClientIds = new Set(prevCasesArr.map(c => c.client_id).filter(Boolean));
    const prevClients = prevClientIds.size;
    const prevCommission = cases
      .filter(c => c.commission_status === 'paid' && yearOf(c.commission_paid_date) === prevYear)
      .reduce((s, c) => s + (c.gross_commission || 0) + (c.client_fee || 0), 0);

    const growth = (curr, prev) => {
      if (prev === 0) return curr > 0 ? null : 0; // null = N/A first year
      return Math.round(((curr - prev) / prev) * 100);
    };

    return {
      yClients,
      yCasesSubmitted,
      yCompletions,
      yPaid,
      totalCommission,
      biggest,
      topMortgageLenders,
      topInsuranceLenders,
      monthCounts,
      busiestMonth,
      monthCommission,
      bestCommissionMonth,
      sortedSources,
      topPartner,
      topArea,
      uniqueAreas,
      sortedTypes,
      prevClients,
      prevCases,
      prevCommission,
      clientsGrowth: growth(yClients.length, prevClients),
      casesGrowth: growth(yCasesSubmitted.length, prevCases),
      commissionGrowth: growth(totalCommission, prevCommission),
    };
  }, [clients, cases, year]);

  // Confetti once data loads
  useEffect(() => {
    if (!loading && !confettiFired.current && yearData.yClients.length + yearData.yCasesSubmitted.length > 0) {
      confettiFired.current = true;
      setTimeout(() => fireConfetti(), 600);
    }
  }, [loading, yearData, fireConfetti]);

  // Re-fire confetti on year change
  useEffect(() => {
    confettiFired.current = false;
  }, [year]);

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin h-8 w-8 border-4 border-red-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  const hasData = yearData.yClients.length + yearData.yCasesSubmitted.length + yearData.yPaid.length > 0;

  if (!hasData) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Year in Review
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">Your business year, summarised.</p>
          </div>
          <YearPicker year={year} setYear={setYear} availableYears={availableYears} open={yearMenuOpen} setOpen={setYearMenuOpen} />
        </div>
        <div className="text-center py-20 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700">
          <Sparkles className="h-12 w-12 mx-auto mb-3 text-slate-300" />
          <p className="text-lg text-slate-600 dark:text-slate-300 font-medium">No activity in {year}</p>
          <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">Pick another year or start adding clients and cases.</p>
        </div>
      </div>
    );
  }

  // Best month index for visual highlighting
  const maxMonthCount = Math.max(...yearData.monthCounts);
  const maxCommMonth = Math.max(...yearData.monthCommission);

  return (
    <div className="relative">
      {/* Sticky top bar */}
      <div className="sticky top-0 z-40 bg-white/90 dark:bg-slate-900/90 backdrop-blur border-b border-slate-200 dark:border-slate-700">
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-500" />
            <p className="font-semibold text-slate-900 dark:text-white" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Year in Review
            </p>
          </div>
          <YearPicker year={year} setYear={setYear} availableYears={availableYears} open={yearMenuOpen} setOpen={setYearMenuOpen} />
        </div>
      </div>

      {/* Snap scroll container */}
      <div className="snap-y snap-mandatory" style={{ scrollSnapType: 'y mandatory' }}>
        {/* Card 1: Hero */}
        <Card gradient="bg-gradient-to-br from-red-600 via-red-500 to-orange-500" id="card-hero">
          <div className="text-center">
            <Sparkles className="h-16 w-16 text-white mx-auto mb-6" />
            <Label>Your</Label>
            <Big>{year}</Big>
            <p className="text-3xl sm:text-5xl font-bold text-white mb-8" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              in review, Kunal
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-12 max-w-3xl mx-auto">
              <Stat icon={Users} value={yearData.yClients.length} label="Active Clients" />
              <Stat icon={Briefcase} value={yearData.yCasesSubmitted.length} label="Applications" />
              <Stat icon={Trophy} value={yearData.yCompletions.length} label="Completions" />
              <Stat icon={PoundSterling} value={fmt(yearData.totalCommission)} label="Earned" />
            </div>
            <p className="text-white/70 text-sm mt-12 animate-bounce">Scroll to continue ↓</p>
          </div>
        </Card>

        {/* Card 2: Biggest deal */}
        {yearData.biggest && (
          <Card gradient="bg-gradient-to-br from-amber-500 via-orange-500 to-red-600" id="card-biggest">
            <div className="text-center">
              <Trophy className="h-16 w-16 text-white mx-auto mb-6" />
              <Label>Your biggest deal</Label>
              <Big>{fmt(yearData.biggest.loan_amount)}</Big>
              <Sub>
                <span className="font-semibold">{yearData.biggest.client_name}</span> with{' '}
                <span className="font-semibold">{yearData.biggest.lender_name || 'a lender'}</span>
                {yearData.biggest.gross_commission ? (
                  <> — earning you <span className="font-semibold">{fmt(yearData.biggest.gross_commission)}</span> in commission</>
                ) : null}
              </Sub>
            </div>
          </Card>
        )}

        {/* Card 3: Top mortgage lender */}
        {yearData.topMortgageLenders.length > 0 && (
          <Card gradient="bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600" id="card-top-lender">
            <div>
              <div className="text-center mb-10">
                <Crown className="h-16 w-16 text-white mx-auto mb-6" />
                <Label>Your favourite mortgage lender</Label>
                <Big>{yearData.topMortgageLenders[0][0]}</Big>
                <Sub>
                  You placed <span className="font-semibold">{yearData.topMortgageLenders[0][1]}</span> case
                  {yearData.topMortgageLenders[0][1] === 1 ? '' : 's'} with them this year
                </Sub>
              </div>
              {yearData.topMortgageLenders.length > 1 && (
                <div className="mt-8 max-w-2xl mx-auto space-y-3">
                  <p className="text-white/70 text-sm uppercase tracking-widest">Top 5 lenders</p>
                  {yearData.topMortgageLenders.map(([name, count], i) => {
                    const max = yearData.topMortgageLenders[0][1];
                    const pct = Math.round((count / max) * 100);
                    return (
                      <div key={name}>
                        <div className="flex items-center justify-between text-white text-sm mb-1">
                          <span className="font-medium">{i + 1}. {name}</span>
                          <span className="text-white/70">{count}</span>
                        </div>
                        <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                          <div className="h-full bg-white rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Card 4: Top insurance provider */}
        {yearData.topInsuranceLenders.length > 0 && (
          <Card gradient="bg-gradient-to-br from-emerald-500 via-teal-600 to-cyan-700" id="card-insurance">
            <div className="text-center">
              <Heart className="h-16 w-16 text-white mx-auto mb-6" />
              <Label>Top insurance provider</Label>
              <Big>{yearData.topInsuranceLenders[0][0]}</Big>
              <Sub>
                <span className="font-semibold">{yearData.topInsuranceLenders[0][1]}</span> polic
                {yearData.topInsuranceLenders[0][1] === 1 ? 'y' : 'ies'} placed
              </Sub>
            </div>
          </Card>
        )}

        {/* Card 5: Busiest month */}
        {maxMonthCount > 0 && (
          <Card gradient="bg-gradient-to-br from-purple-600 via-pink-600 to-rose-600" id="card-busiest">
            <div>
              <div className="text-center mb-10">
                <Calendar className="h-16 w-16 text-white mx-auto mb-6" />
                <Label>Your busiest month</Label>
                <Big>{monthNames[yearData.busiestMonth]}</Big>
                <Sub>
                  <span className="font-semibold">{maxMonthCount}</span> applications submitted
                </Sub>
              </div>
              <div className="flex items-end justify-center gap-2 sm:gap-3 max-w-3xl mx-auto h-48">
                {yearData.monthCounts.map((count, m) => {
                  const pct = maxMonthCount ? (count / maxMonthCount) * 100 : 0;
                  const isPeak = m === yearData.busiestMonth;
                  return (
                    <div key={m} className="flex-1 flex flex-col items-center justify-end h-full">
                      <div
                        className={`w-full rounded-t transition-all duration-700 ${isPeak ? 'bg-white' : 'bg-white/40'}`}
                        style={{ height: `${pct}%` }}
                      />
                      <span className="text-[10px] sm:text-xs text-white/80 mt-1">{monthNames[m]}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>
        )}

        {/* Card 6: Best commission month */}
        {maxCommMonth > 0 && (
          <Card gradient="bg-gradient-to-br from-yellow-500 via-amber-500 to-orange-600" id="card-best-comm">
            <div className="text-center">
              <PoundSterling className="h-16 w-16 text-white mx-auto mb-6" />
              <Label>Your top earning month</Label>
              <Big>{monthNames[yearData.bestCommissionMonth]}</Big>
              <Sub>
                You earned <span className="font-semibold">{fmt(maxCommMonth)}</span> in a single month
              </Sub>
            </div>
          </Card>
        )}

        {/* Card 7: Lead sources */}
        {yearData.sortedSources.length > 0 && (
          <Card gradient="bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900" id="card-sources">
            <div>
              <div className="text-center mb-10">
                <Users className="h-16 w-16 text-white mx-auto mb-6" />
                <Label>Where your business came from</Label>
                <Big className="text-3xl">
                  {yearData.sortedSources[0][0].replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </Big>
                <Sub>
                  was your top source — <span className="font-semibold">{yearData.sortedSources[0][1]}</span> client
                  {yearData.sortedSources[0][1] === 1 ? '' : 's'}
                </Sub>
              </div>
              <div className="max-w-2xl mx-auto space-y-3 mt-8">
                {yearData.sortedSources.slice(0, 6).map(([src, count]) => {
                  const max = yearData.sortedSources[0][1];
                  const pct = Math.round((count / max) * 100);
                  return (
                    <div key={src}>
                      <div className="flex items-center justify-between text-white text-sm mb-1">
                        <span className="font-medium">
                          {src.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                        <span className="text-white/70">{count}</span>
                      </div>
                      <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                        <div className="h-full bg-white rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>
        )}

        {/* Card 8: Top referral partner */}
        {yearData.topPartner && (
          <Card gradient="bg-gradient-to-br from-pink-500 via-rose-500 to-red-500" id="card-partner">
            <div className="text-center">
              <Award className="h-16 w-16 text-white mx-auto mb-6" />
              <Label>Your top referral partner</Label>
              <Big>{yearData.topPartner[0]}</Big>
              <Sub>
                Sent you <span className="font-semibold">{yearData.topPartner[1]}</span> client
                {yearData.topPartner[1] === 1 ? '' : 's'} this year — thank them!
              </Sub>
            </div>
          </Card>
        )}

        {/* Card 9: Geographic spread */}
        {yearData.topArea && (
          <Card gradient="bg-gradient-to-br from-cyan-500 via-blue-600 to-indigo-700" id="card-area">
            <div className="text-center">
              <MapPin className="h-16 w-16 text-white mx-auto mb-6" />
              <Label>You worked across</Label>
              <Big>{yearData.uniqueAreas} <span className="text-3xl sm:text-5xl">postcode areas</span></Big>
              <Sub>
                Your most active area was <span className="font-semibold">{yearData.topArea[0]}</span> with{' '}
                <span className="font-semibold">{yearData.topArea[1]}</span> clients
              </Sub>
            </div>
          </Card>
        )}

        {/* Card 10: Year-on-year growth */}
        <Card gradient="bg-gradient-to-br from-green-600 via-emerald-600 to-teal-700" id="card-growth">
          <div className="text-center">
            <TrendingUp className="h-16 w-16 text-white mx-auto mb-6" />
            <Label>Year on year</Label>
            <Big>vs {year - 1}</Big>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-12 max-w-3xl mx-auto">
              <GrowthStat label="Clients" current={yearData.yClients.length} prev={yearData.prevClients} growth={yearData.clientsGrowth} />
              <GrowthStat label="Applications" current={yearData.yCasesSubmitted.length} prev={yearData.prevCases} growth={yearData.casesGrowth} />
              <GrowthStat label="Commission" current={fmt(yearData.totalCommission)} prev={fmt(yearData.prevCommission)} growth={yearData.commissionGrowth} />
            </div>
          </div>
        </Card>

        {/* Card 11: Closing */}
        <Card gradient="bg-gradient-to-br from-red-600 via-pink-600 to-purple-700" id="card-closing">
          <div className="text-center">
            <Sparkles className="h-20 w-20 text-white mx-auto mb-8 animate-pulse" />
            <p className="text-2xl sm:text-3xl text-white/80 mb-4 uppercase tracking-widest">Here's to</p>
            <Big>{year + 1}</Big>
            <Sub>Thanks for an incredible {year}, Kunal. Keep building. 🚀</Sub>
            <Button
              onClick={() => fireConfetti()}
              className="mt-12 bg-white text-red-600 hover:bg-white/90 text-lg px-8 py-6"
            >
              <Sparkles className="h-5 w-5 mr-2" />
              Celebrate again
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

// ── Sub components ──────────────────────────────────────────────────────────

const Stat = ({ icon: Icon, value, label }) => (
  <div className="bg-white/15 backdrop-blur rounded-xl p-4">
    <Icon className="h-6 w-6 text-white mx-auto mb-2" />
    <p className="text-white text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Plus Jakarta Sans' }}>{value}</p>
    <p className="text-white/80 text-xs uppercase tracking-wide mt-1">{label}</p>
  </div>
);

const GrowthStat = ({ label, current, prev, growth }) => {
  const positive = growth !== null && growth > 0;
  const negative = growth !== null && growth < 0;
  return (
    <div className="bg-white/15 backdrop-blur rounded-xl p-6">
      <p className="text-white/80 text-sm uppercase tracking-wide mb-2">{label}</p>
      <p className="text-white text-3xl font-bold mb-1" style={{ fontFamily: 'Plus Jakarta Sans' }}>{current}</p>
      <p className="text-white/60 text-xs">was {prev}</p>
      {growth !== null ? (
        <p className={`text-lg font-bold mt-2 ${positive ? 'text-green-200' : negative ? 'text-red-200' : 'text-white/70'}`}>
          {positive ? '↑' : negative ? '↓' : '–'} {Math.abs(growth)}%
        </p>
      ) : (
        <p className="text-white/60 text-xs mt-2 italic">First year</p>
      )}
    </div>
  );
};

const YearPicker = ({ year, setYear, availableYears, open, setOpen }) => (
  <div className="relative">
    <button
      onClick={() => setOpen(!open)}
      className="flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700"
    >
      Viewing {year}
      <ChevronDown className="h-4 w-4" />
    </button>
    {open && (
      <div className="absolute right-0 mt-1 w-32 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-50 overflow-hidden">
        {availableYears.map(y => (
          <button
            key={y}
            onClick={() => { setYear(y); setOpen(false); }}
            className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 ${y === year ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 font-semibold' : 'text-slate-700 dark:text-slate-200'}`}
          >
            {y}
          </button>
        ))}
      </div>
    )}
  </div>
);

export default YearInReview;
